import { z } from 'zod';
import type { GameAction, GameState, PlayerConfig } from '@/engine/types';
import type { PlayerColorId } from '@/lib/options';

export const PROTOCOL_VERSION = 3;
export const ROOM_CODE_LENGTH = 5;
export const MAX_NAME = 24;
/** Host seat(s) plus remote guests may not exceed the engine's seat cap. */
export const MAX_REMOTE_GUESTS = 5;

export const playerColorSchema = z.enum([
  'cyan',
  'magenta',
  'lime',
  'amber',
  'violet',
  'coral',
]);

export const nameSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_NAME)
  .transform(s => s.replace(/\s+/g, ' '));

export type SeatStatus = 'connected' | 'disconnected';

export interface RemoteSeat {
  playerId: string;
  name: string;
  color: PlayerColorId;
  status: SeatStatus;
}

const seatSchema = z.object({
  playerId: z.string().min(1).max(32),
  name: nameSchema,
  color: playerColorSchema,
  status: z.enum(['connected', 'disconnected']),
});

/** Strict: the host only accepts well-formed actions. */
export const gameActionSchema: z.ZodType<GameAction> = z.discriminatedUnion(
  'type',
  [
    z.object({ type: z.literal('roll') }),
    z.object({ type: z.literal('undoScore') }),
    z.object({
      type: z.literal('toggleHold'),
      index: z.number().int().min(0).max(7),
    }),
    z.object({
      type: z.literal('setHolds'),
      held: z.array(z.boolean()).max(8),
    }),
    z.object({
      type: z.literal('score'),
      categoryId: z.string().min(1).max(40),
      col: z.number().int().min(0).max(2).optional(),
      word: z.string().max(7).optional(),
    }),
    z.object({
      type: z.literal('usePower'),
      power: z.enum(['reroll', 'flip', 'nudge']),
      index: z.number().int().min(0).max(7).optional(),
      delta: z.union([z.literal(1), z.literal(-1)]).optional(),
    }),
  ]
);

/**
 * Loose: clients trust the host, so this only guards against garbage
 * shapes that would crash the renderer.
 */
export const gameStateSchema = z.looseObject({
  id: z.string(),
  variantId: z.string(),
  seed: z.number(),
  players: z.array(
    z.looseObject({ id: z.string(), name: z.string(), kind: z.string() })
  ),
  round: z.number(),
  turn: z.number(),
  dice: z.array(z.looseObject({ value: z.number(), held: z.boolean() })),
  rollsUsed: z.number(),
  phase: z.enum(['playing', 'finished']),
  version: z.number(),
});

const base = { v: z.literal(PROTOCOL_VERSION) };

export const messageSchema = z.discriminatedUnion('t', [
  z.object({
    ...base,
    t: z.literal('hello'),
    name: nameSchema,
    color: playerColorSchema,
    /** Reclaim a seat after a reconnect. */
    playerId: z.string().max(32).optional(),
  }),
  z.object({
    ...base,
    t: z.literal('lobby'),
    hostName: nameSchema,
    variantId: z.string().nullable(),
    seats: z.array(seatSchema),
    inGame: z.boolean(),
  }),
  z.object({
    ...base,
    t: z.literal('assign'),
    playerId: z.string().min(1).max(32),
  }),
  z.object({ ...base, t: z.literal('start'), game: gameStateSchema }),
  z.object({ ...base, t: z.literal('state'), game: gameStateSchema }),
  z.object({
    ...base,
    t: z.literal('action'),
    action: gameActionSchema,
    playerId: z.string().min(1).max(32),
  }),
  z.object({ ...base, t: z.literal('ping') }),
  z.object({ ...base, t: z.literal('pong') }),
  /** Host (re)attached: every guest must re-hello so seats and state resync. */
  z.object({ ...base, t: z.literal('who') }),
  z.object({ ...base, t: z.literal('kick'), reason: z.string().max(120) }),
  z.object({ ...base, t: z.literal('ended') }),
  z.object({
    ...base,
    t: z.literal('chat'),
    from: nameSchema,
    text: z.string().trim().min(1).max(200),
  }),
]);

export type Message = z.infer<typeof messageSchema>;
export type MessageOf<T extends Message['t']> = Extract<Message, { t: T }>;

export function parseMessage(raw: unknown): Message | null {
  const r = messageSchema.safeParse(raw);
  return r.success ? r.data : null;
}

/** Build a message with the protocol version stamped on. */
export function msg<T extends Message['t']>(
  t: T,
  body: Omit<MessageOf<T>, 't' | 'v'>
): MessageOf<T> {
  return { v: PROTOCOL_VERSION, t, ...body } as MessageOf<T>;
}

export function stateMessage(t: 'start' | 'state', game: GameState): Message {
  return { v: PROTOCOL_VERSION, t, game } as unknown as Message;
}

export function seatToPlayer(seat: RemoteSeat): PlayerConfig {
  return {
    id: seat.playerId,
    name: seat.name,
    color: seat.color,
    kind: 'remote',
  };
}

const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

export function normalizeCode(input: string): string {
  return input
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0')
    .replace(/1/g, '')
    .replace(/0/g, '')
    .slice(0, ROOM_CODE_LENGTH);
}

export function isValidCode(code: string): boolean {
  return (
    code.length === ROOM_CODE_LENGTH &&
    [...code].every(c => CODE_ALPHABET.includes(c))
  );
}

export function randomCode(random: () => number = Math.random): string {
  let out = '';
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
    out += CODE_ALPHABET[Math.floor(random() * CODE_ALPHABET.length)] ?? 'A';
  }
  return out;
}

export function joinUrlFor(code: string): string {
  let origin = typeof location !== 'undefined' ? location.origin : '';
  // In dev, a host opened on localhost would hand phones an unreachable link;
  // Vite injects the machine's LAN address for that case.
  if (
    typeof __DEV_LAN_ORIGIN__ === 'string' &&
    /^(localhost|127\.0\.0\.1)$/.test(location.hostname)
  ) {
    origin = __DEV_LAN_ORIGIN__;
  }
  return `${origin}/join/${code}`;
}
